import { useState, useEffect } from "react";

// Editor for a dwell zone (resting place / furniture). A still person is only
// reliably detected within a sensor's inner ring, so the `covered` flag reflects
// the reliable ring, not the full presence cone.
function DwellEditor({ zone, roomName, covered, onUpdate, onDelete, onDeselect }) {
  const [label, setLabel] = useState(zone.label || "");
  const [w, setW] = useState(String(zone.w ?? 1));
  const [h, setH] = useState(String(zone.h ?? 1));

  useEffect(() => {
    setLabel(zone.label || "");
    setW(String(zone.w ?? 1));
    setH(String(zone.h ?? 1));
  }, [zone.id, zone.label, zone.w, zone.h]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const commitSize = (key, raw, fallback) => {
    const v = parseFloat(raw);
    if (!isNaN(v) && v >= 0.2) onUpdate({ [key]: Math.round(v * 100) / 100 });
    else (key === "w" ? setW : setH)(String(fallback));
  };

  return (
    <div className="room-editor">
      <div className="room-editor-header">
        <h3>Resting Place</h3>
        <button className="btn-icon" onClick={onDeselect} title="Deselect">
          ✕
        </button>
      </div>

      <div className="room-editor-field">
        <label>Label</label>
        <input
          type="text"
          value={label}
          placeholder="Couch, Desk, Bed…"
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => onUpdate({ label: label.trim() || "Resting place" })}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="room-editor-field">
        <label>Room</label>
        <div className="readonly-field">{roomName || "— (outside rooms)"}</div>
      </div>

      <div className="dim-row">
        <div className="dim-field">
          <label>Width (m)</label>
          <input
            type="number"
            step="0.1"
            min="0.2"
            value={w}
            onChange={(e) => setW(e.target.value)}
            onBlur={() => commitSize("w", w, zone.w ?? 1)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="dim-field">
          <label>Depth (m)</label>
          <input
            type="number"
            step="0.1"
            min="0.2"
            value={h}
            onChange={(e) => setH(e.target.value)}
            onBlur={() => commitSize("h", h, zone.h ?? 1)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      <div className="room-editor-field">
        <label>Reliable detection</label>
        <div className={`dwell-status ${covered ? "dwell-ok" : "dwell-bad"}`}>
          {covered ? "✓ Within a 2 m reliable ring" : "✗ No sensor reaches this seat reliably"}
        </div>
      </div>

      <p className="editor-hint">
        Drag the zone on the map to position it. Seated people are only detected
        within a sensor's inner reliable ring (~2 m).
      </p>

      <div className="room-editor-actions">
        <button className="btn-secondary btn-danger" onClick={onDelete}>
          Delete Resting Place
        </button>
      </div>
    </div>
  );
}

export default DwellEditor;
